import { useState, useMemo } from "react";
import { Globe, Check, Search } from "lucide-react";
import { useCurrency } from "@/contexts/CurrencyContext";

// Curated shortlist shown at the top of the picker. The full list (~150) lives
// below a divider — discoverable but not in the visitor's face.
const TOP = ["USD", "GBP", "EUR", "CAD", "AUD", "NZD", "JPY", "AED", "SGD", "INR"];

export default function CurrencyPicker({ align = "right" }) {
  const { currency, symbol, supported, setCurrency, ready, country } = useCurrency();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const needle = q.trim().toUpperCase();
    if (!needle) return supported;
    return supported.filter((c) => c.code.includes(needle));
  }, [q, supported]);

  const top = useMemo(
    () => TOP.map((code) => supported.find((c) => c.code === code)).filter(Boolean),
    [supported]
  );

  if (!ready) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-full glass-card text-zinc-300 hover:text-white font-['JetBrains_Mono'] text-[11px] tracking-wider uppercase transition hover:border-[#D946EF]/30 hover:shadow-[0_0_20px_rgba(217,70,239,0.08)]"
        data-testid="currency-picker-trigger"
        aria-label={`Currency: ${currency}`}
      >
        <Globe className="w-3 h-3 opacity-70" />
        <span className="text-white font-bold">{symbol}</span>
        <span>{currency}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className={`absolute top-full mt-2 w-[260px] z-50 rounded-xl bg-[#030303]/95 backdrop-blur-xl shadow-2xl overflow-hidden ${align === "right" ? "right-0" : "left-0"}`}
            style={{ border: "1px solid transparent", backgroundClip: "padding-box", boxShadow: "0 0 0 1px rgba(217,70,239,0.15), 0 25px 50px -12px rgba(0,0,0,0.5)" }}
            data-testid="currency-picker-panel"
          >
            <div className="p-3 border-b border-white/5">
              <p className="font-['JetBrains_Mono'] text-[9px] tracking-[0.25em] uppercase text-[#D946EF] mb-1">Display currency</p>
              <p className="font-['Libre_Baskerville'] italic text-[11px] text-zinc-500">
                {country ? `Detected in ${country}` : "Pick how you want to see prices"}
              </p>
            </div>

            <div className="p-2 border-b border-white/5">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search currency..."
                  className="w-full h-8 pl-7 pr-2 rounded-md bg-white/5 border border-white/5 focus:border-[#D946EF]/50 focus:shadow-[0_0_15px_rgba(217,70,239,0.1)] outline-none text-white text-xs font-['Manrope'] placeholder:text-zinc-600 transition-shadow"
                  data-testid="currency-picker-search"
                />
              </div>
            </div>

            <div className="max-h-[280px] overflow-y-auto">
              {!q && top.length > 0 && (
                <div className="py-1">
                  <p className="px-3 pt-2 pb-1 font-['JetBrains_Mono'] text-[9px] tracking-widest uppercase gradient-text">Popular</p>
                  {top.map((c) => <Row key={c.code} c={c} active={c.code === currency} onSelect={(code) => { setCurrency(code); setOpen(false); }} />)}
                  {/* Gradient divider between Popular and All */}
                  <div className="mx-3 my-1 h-px" style={{ background: "linear-gradient(90deg, transparent, #D946EF, #8B5CF6, transparent)" }} />
                  <p className="px-3 pt-1 pb-1 font-['JetBrains_Mono'] text-[9px] tracking-widest uppercase text-zinc-600">All currencies</p>
                </div>
              )}
              {filtered.length === 0 ? (
                <p className="p-6 text-center font-['Manrope'] text-xs text-zinc-500">No match for "{q}"</p>
              ) : (
                filtered.map((c) => <Row key={c.code} c={c} active={c.code === currency} onSelect={(code) => { setCurrency(code); setOpen(false); }} />)
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ c, active, onSelect }) {
  return (
    <button
      onClick={() => onSelect(c.code)}
      className={`w-full flex items-center gap-3 px-3 py-2 text-left transition ${active ? "bg-gradient-to-r from-[#D946EF]/10 via-[#8B5CF6]/10 to-transparent" : "hover:bg-white/5"}`}
      data-testid={`currency-option-${c.code}`}
    >
      <span className="font-['JetBrains_Mono'] text-white text-sm w-5 text-center">{c.symbol}</span>
      <span className="font-['Manrope'] text-sm text-white flex-1">{c.code}</span>
      {active && <Check className="w-4 h-4 text-[#D946EF] drop-shadow-[0_0_6px_rgba(217,70,239,0.5)]" />}
    </button>
  );
}
